import React, { FC, useEffect, useState } from 'react';
import { Button, Label, Modal, Spinner } from 'flowbite-react';
import { t } from 'i18next';
import { useAddUserMutation } from '../../redux/api/UserListApi';
import { EditTextInput } from '../Shared/EditTextInput';
import { RoleSelect } from '../RoleSelect/RoleSelect';
import { DashboardUserRole } from '../../common/enums/DashboardUserRole';
import { HttpStatus } from '../../common/enums/HttpStatus';
import { getErrorStatus } from '../Shared/ErrorComponent/ErrorComponent';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const emptyForm = { name: '', email: '', role: DashboardUserRole.User };

const AddUserModal: FC<Props> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [emailOnStaff, setEmailOnStaff] = useState(false);
  const [addUser, { isLoading }] = useAddUserMutation();

  useEffect(() => {
    if (!isOpen) {
      setFormData(emptyForm);
      setEmailOnStaff(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      return;
    }

    try {
      await addUser(formData).unwrap();
      onClose();
    } catch (error) {
      // The toast middleware reports the failure; the modal stays open with the input.
      setEmailOnStaff(getErrorStatus(error) === HttpStatus.Conflict);
    }
  };

  return (
    <Modal show={isOpen} size="4xl" onClose={onClose}>
      <Modal.Header className="modal-header">
        <div className="flex-1">{t('usersManagement.addUser')}</div>
      </Modal.Header>
      <Modal.Body>
        <form id="addUserForm" onSubmit={handleSubmit}>
          <div className="space-y-6">
            <EditTextInput
              label={t('usersManagement.name')}
              type="text"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              placeholder={t('usersManagement.name')}
            />

            <div>
              <EditTextInput
                label={t('usersManagement.email')}
                value={formData.email}
                type="email"
                aria-invalid={emailOnStaff}
                aria-describedby={
                  emailOnStaff ? 'addUser-email-error' : undefined
                }
                onChange={(value) => {
                  setEmailOnStaff(false);
                  setFormData({ ...formData, email: value });
                }}
                placeholder={t('usersManagement.email')}
              />
              {emailOnStaff && (
                <p
                  id="addUser-email-error"
                  role="alert"
                  className="mt-2 text-sm text-red-600 dark:text-red-400"
                >
                  {t('usersManagement.emailOnStaff')}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="addUser-role" value={t('usersManagement.role')} />
              <RoleSelect
                id="addUser-role"
                value={formData.role}
                onChange={(role) => setFormData({ ...formData, role })}
              />
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-between gap-4">
          <Button
            gradientMonochrome="success"
            type="submit"
            form="addUserForm"
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner color="success" size="sm" />
            ) : (
              t('content.save')
            )}
          </Button>
          <Button color="gray" onClick={onClose} disabled={isLoading}>
            {t('media.close')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModal;
